import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Accordion, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FieldArray, Formik } from 'formik';
import _ from 'lodash';
import axios from 'axios';
import styled from 'styled-components';
import ReactDOMServer from 'react-dom/server';
import { AppNotificationAlert, AppRemixIcon } from '@/layout/AppComponents';
import Translator from '../../../../layout/components/helpers/Translator';
import BlockScorecard from '../../../../layout/screenSpecifics/Modules/SaicaReports/ModuleSaicaReportHomeScreen/BlockScoreboard';
import BlockIndicentsAccidents from '../../../../layout/screenSpecifics/Modules/SaicaReports/ModuleSaicaReportHomeScreen/BlockIndicentsAccidents';
import BlockHSMasterPlanProgress from '../../../../layout/screenSpecifics/Modules/SaicaReports/ModuleSaicaReportHomeScreen/BlockHSMasterPlanProgress';
import BlockHSFrameworkProgress from '../../../../layout/screenSpecifics/Modules/SaicaReports/ModuleSaicaReportHomeScreen/BlockHSFrameworkProgress';
import BlockHSRiskRegister from '../../../../layout/screenSpecifics/Modules/SaicaReports/ModuleSaicaReportHomeScreen/BlockHSRiskRegister';
import BlockHSLegalRegister from '../../../../layout/screenSpecifics/Modules/SaicaReports/ModuleSaicaReportHomeScreen/BlockHSLegalRegister';
import BlockOtherPoints from '../../../../layout/screenSpecifics/Modules/SaicaReports/ModuleSaicaReportHomeScreen/BlockOtherPoints';
import AppModal from '../../../../layout/components/Modal/AppModal';
import DeleteModal from '../../../../layout/components/Modal/DeleteModal';
import { path, axiosPath } from '../../../../routing/helpers/router';
import Sidebar from '../../../../layout/Sidebar/Sidebar';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import SidebarButton from '../../../../layout/Sidebar/SidebarButton';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';
import colors from '@/constants/colors';
import errorHandler from '@/layout/components/error/ErrorHandler';

const ModuleSaicaReportReportScreen = () => {
  const { moduleBridgeHex, reportHex } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const global = useSelector((state) => state.global);
  const incidentsAccidentsButton = useRef(null);
  const hsMasterPlanProgressButton = useRef(null);
  const hsFrameworkProgressButton = useRef(null);
  const hsRiskRegisterButton = useRef(null);
  const hsLegalRegisterButton = useRef(null);
  const otherPointsButton = useRef(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [report, setReport] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showUpdateObligationsModal, setShowUpdateObligationsModal] =
    useState(false);
  const [showUpdateTasksModal, setShowUpdateTasksModal] = useState(false);
  const [reportInitialValues, setReportInitialValues] = useState({
    scorecard: {
      originTier: '',
      siTarget: '',
      safetyIndex: ['', '', '', '', '', ''],
      ltafr: ['', '', '', '', '', ''],
      dl: ['', '', '', '', '', ''],
      lta: ['', '', '', '', '', ''],
      mt: ['', '', '', '', '', ''],
      fa: ['', '', '', '', '', ''],
      comment: '',
    },
    incidentsAccidents: [],
    hsMasterPlanProgress: [],
    hsFrameworkProgress: [],
    hsRiskRegister: [],
    hsLegalRegister: [],
    otherPoints: [],
    exit: false,
  });
  const blankScorecard = {
    originTier: '',
    siTarget: '',
    safetyIndex: ['', '', '', '', '', ''],
    ltafr: ['', '', '', '', '', ''],
    dl: ['', '', '', '', '', ''],
    lta: ['', '', '', '', '', ''],
    mt: ['', '', '', '', '', ''],
    fa: ['', '', '', '', '', ''],
    comment: '',
  };

  const loadReport = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_saicaReports_report'),
        { moduleBridgeHex, reportHex },
      );
      return response.data;
    } catch (err) {}
  });

  useEffect(() => {
    loadReport().then((response) => {
      if (isMounted.current) {
        setReport(response);
        if (response.settings) setReportInitialValues(response.settings);
        setIsComplete(response.status === 3);
      }
    });
  }, [moduleBridgeHex, reportHex]);

  const handleRemoveReport = async () => {
    try {
      await axios.post(
        axiosPath('api_r_user_module_saicaReports_removeReport'),
        { moduleBridgeHex, reportHex },
      );
      navigate(
        path({
          name: 'user_module_saicaReport_home',
          params: { moduleBridgeHex, level: 4 },
        }),
      );
    } catch (err) {}
  };

  const handleSubmitReport = async (values, markAsComplete) => {
    setSaveLoading(true);
    const { exit, ...valuesWithoutExit } = values;
    try {
      await axios.post(axiosPath('api_r_user_module_saicaReports_saveReport'), {
        moduleBridgeHex,
        reportHex,
        values: valuesWithoutExit,
        markAsComplete,
      });
      if (exit)
        navigate(
          path({
            name: 'user_module_saicaReport_home',
            params: { moduleBridgeHex, level: 4 },
          }),
        );
      AppNotificationAlert({
        title: ReactDOMServer.renderToString(
          <Translator file="admin" name="saved" />,
        ),
        message: ReactDOMServer.renderToString(
          <Translator file="admin" name="report-saved" />,
        ),
        type: 'success',
      });
      setSaveLoading(false);
    } catch (err) {}
  };

  const handleMarkAsComplete = (values) => {
    handleSubmitReport(values, true);
  };

  const handleLevelUp = async (values) => {
    try {
      await axios.post(axiosPath('api_r_user_module_saicaReports_levelUp'), {
        moduleBridgeHex,
        reportHex,
        values,
      });
      return true;
    } catch (err) {}
  };

  const handleUpdateReport = async (type) => {
    try {
      await axios.post(
        axiosPath('api_r_user_module_saicaReports_updateReport'),
        { moduleBridgeHex, reportHex, type },
      );
      navigate(
        path({
          name: 'user_module_saicaReport_home',
          params: { moduleBridgeHex },
        }),
      );
    } catch (err) {}
  };

  const handleGetWordFile = useCallback(async () => {
    axios
      .post(axiosPath('api_r_user_module_saicaReports_report_export_word'), {
        moduleBridgeHex,
        reportHex,
      })
      .then(({ data }) => {
        window.open(data, '_blank', 'noopener,noreferrer');
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, reportHex]);
  
  const handleGetXlsFile = useCallback(async () => {
    axios
      .post(axiosPath('api_r_user_module_saicaReports_report_export_xls'), {
        moduleBridgeHex,
        reportHex,
      })
      .then(({ data }) => {
        window.open(data, '_blank', 'noopener,noreferrer');
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, reportHex]);

  if (!report) {
    return (
      <div className="pl-2">
        <div className="content-header" />
        <section className="content">
          <Sidebar />
        </section>
      </div>
    );
  }

  return (
    <div className="pl-2">
      <div className="content-header">
        <BreadcrumbHandler
          paths={[
            {
              name: 'H&S REPORTING TIER’S',
              route: {
                name: 'user_module_saicaReport_home',
                params: { moduleBridgeHex, level: 4 },
              },
            },
          ]}
        />
      </div>
      <section className="content">
        {report && (
          <Formik
            enableReinitialize
            initialValues={reportInitialValues}
            onSubmit={(values) => handleSubmitReport(values, false)}
          >
            {({ setFieldValue, handleSubmit, values }) => (
              <>
                <Sidebar>
                  <Container>
                    <Accordion
                      // className="nav nav-pills nav-sidebar flex-column nav-flat nav-compact nav-child-indent"
                      // data-widget="treeview"
                      // role="menu"
                      // data-accordion="false"
                      defaultActiveKey={report.status !== 3 ? '0' : '2'}
                    >
                      <Container>
                        <Accordion.Item
                          // as="div"
                          eventKey="0"
                        >
                          <Accordion.Header
                            as="h9"
                            className="panel-title pointer m-0"
                          >
                            <AppRemixIcon icon="add-box" />
                            <Translator
                              file="module-saicaReports"
                              name="add.content"
                            />
                          </Accordion.Header>
                          <Accordion.Body>
                            <>
                              <div
                                onClick={() =>
                                  !isComplete &&
                                  incidentsAccidentsButton.current.click()
                                }
                              >
                                <SidebarButton
                                  title={
                                    <AddContainer>
                                      <span>
                                        <Translator
                                          file="module-saicaReports"
                                          name="incidents.and.accidents"
                                        />
                                      </span>
                                      {values.incidentsAccidents.length > 0 && (
                                        <span className="badge badge-info right">
                                          {values.incidentsAccidents.length}
                                        </span>
                                      )}
                                    </AddContainer>
                                  }
                                />
                              </div>
                              <div
                                onClick={() =>
                                  !isComplete &&
                                  hsMasterPlanProgressButton.current.click()
                                }
                              >
                                <SidebarButton
                                  title={
                                    <AddContainer>
                                      <span>
                                        <Translator
                                          file="module-saicaReports"
                                          name="master.plan.progress"
                                        />
                                      </span>
                                      {values.hsMasterPlanProgress.length >
                                        0 && (
                                        <span className="badge badge-info right">
                                          {values.hsMasterPlanProgress.length}
                                        </span>
                                      )}
                                    </AddContainer>
                                  }
                                />
                              </div>
                              <div
                                onClick={() =>
                                  !isComplete &&
                                  hsFrameworkProgressButton.current.click()
                                }
                              >
                                <SidebarButton
                                  title={
                                    <AddContainer>
                                      <span>
                                        <Translator
                                          file="module-saicaReports"
                                          name="framework.progress"
                                        />
                                      </span>
                                      {values.hsFrameworkProgress.length >
                                        0 && (
                                        <span className="badge badge-info right">
                                          {values.hsFrameworkProgress.length}
                                        </span>
                                      )}
                                    </AddContainer>
                                  }
                                />
                              </div>
                              <div
                                onClick={() =>
                                  !isComplete &&
                                  hsRiskRegisterButton.current.click()
                                }
                              >
                                <SidebarButton
                                  title={
                                    <AddContainer>
                                      <span>
                                        <Translator
                                          file="module-saicaReports"
                                          name="risk.register"
                                        />
                                      </span>
                                      {values.hsRiskRegister.length > 0 && (
                                        <span className="badge badge-info right">
                                          {values.hsRiskRegister.length}
                                        </span>
                                      )}
                                    </AddContainer>
                                  }
                                />
                              </div>
                              <div
                                onClick={() =>
                                  !isComplete &&
                                  hsLegalRegisterButton.current.click()
                                }
                              >
                                <SidebarButton
                                  title={
                                    <AddContainer>
                                      <span>
                                        <Translator
                                          file="module-saicaReports"
                                          name="legal.register"
                                        />
                                      </span>
                                      {values.hsLegalRegister.length > 0 && (
                                        <span className="badge badge-info right">
                                          {values.hsLegalRegister.length}
                                        </span>
                                      )}
                                    </AddContainer>
                                  }
                                />
                              </div>
                              <div
                                onClick={() =>
                                  !isComplete &&
                                  otherPointsButton.current.click()
                                }
                              >
                                <SidebarButton
                                  title={
                                    <AddContainer>
                                      <span>
                                        <Translator
                                          file="module-saicaReports"
                                          name="other.points"
                                        />
                                      </span>
                                      {values.otherPoints.length > 0 && (
                                        <span className="badge badge-info right">
                                          {values.otherPoints.length}
                                        </span>
                                      )}
                                    </AddContainer>
                                  }
                                />
                              </div>
                            </>
                          </Accordion.Body>
                        </Accordion.Item>
                      </Container>
                      <Container>
                        <Accordion.Item as="div" eventKey="1">
                          <Accordion.Header
                            as="h9"
                            className="panel-title pointer m-0"
                          >
                            <AppRemixIcon icon="wrench" />
                            <Translator
                              file="module-saicaReports"
                              name="options"
                            />
                          </Accordion.Header>
                          <Accordion.Body>
                            <>
                              {report.plantGroup.level === 4 && (
                                <>
                                  <div
                                    onClick={() =>
                                      !isComplete &&
                                      setShowUpdateObligationsModal(true)
                                    }
                                  >
                                    <SidebarButton
                                      title={
                                        <AddContainer>
                                          <span>
                                            <Translator
                                              file="module-saicaReports"
                                              name="update.legal.register"
                                            />
                                          </span>
                                        </AddContainer>
                                      }
                                    />
                                  </div>
                                  <div
                                    onClick={() =>
                                      !isComplete &&
                                      setShowUpdateTasksModal(true)
                                    }
                                  >
                                    <SidebarButton
                                      title={
                                        <AddContainer>
                                          <span>
                                            <Translator
                                              file="module-saicaReports"
                                              name="update.framework.progress"
                                            />
                                          </span>
                                        </AddContainer>
                                      }
                                    />
                                  </div>
                                </>
                              )}
                              <div onClick={() => setShowRemoveModal(true)}>
                                <SidebarButton
                                  title={
                                    <AddContainer>
                                      <span className="text-danger">
                                        <Translator
                                          file="module-saicaReports"
                                          name="delete.report"
                                        />
                                      </span>
                                    </AddContainer>
                                  }
                                />
                              </div>
                            </>
                          </Accordion.Body>
                        </Accordion.Item>
                      </Container>
                      <Container>
                        <Accordion.Item as="div" eventKey="2">
                          <Accordion.Header
                            as="h9"
                            className="panel-title pointer m-0"
                          >
                            <AppRemixIcon icon="folder-shared" />
                            <Translator
                              file="module-saicaReports"
                              name="export.report"
                            />
                          </Accordion.Header>
                          <Accordion.Body>
                            <>
                              <OverlayTrigger
                                placement="right"
                                overlay={
                                  <Tooltip>
                                    <Translator
                                      file="module-saicaReports"
                                      name="information.last.save.exported"
                                    />
                                  </Tooltip>
                                }
                              >
                                <div
                                  onClick={() =>
                                    !isComplete &&
                                    setShowUpdateObligationsModal(true)
                                  }
                                >
                                  <ButtonSidebar
                                    className="pointer"
                                    onClick={() => handleGetXlsFile()}
                                  >
                                    <AddContainer>
                                      <span className="sb-title">
                                        <Translator
                                          file="module-saicaReports"
                                          name="export.xls"
                                        />
                                      </span>
                                    </AddContainer>
                                  </ButtonSidebar>

                                  {/* <SidebarButton
                                    title={
                                      <AddContainer>
                                        <span>
                                          <Translator
                                            file="module-saicaReports"
                                            name="export.xls"
                                          />
                                        </span>
                                      </AddContainer>
                                    }
                                    onClick={() => handleGetXlsFile()}
                                    // href={`${global.urlData.rootProject}saica-reports/export-xls-report/${moduleBridgeHex}/${reportHex}/`}
                                  /> */}
                                </div>
                              </OverlayTrigger>
                              <OverlayTrigger
                                placement="right"
                                overlay={
                                  <Tooltip>
                                    <Translator
                                      file="module-saicaReports"
                                      name="information.last.save.exported"
                                    />
                                  </Tooltip>
                                }
                              >
                                <div
                                  onClick={() =>
                                    !isComplete &&
                                    setShowUpdateObligationsModal(true)
                                  }
                                >
                                  <ButtonSidebar
                                    className="pointer"
                                    onClick={() => handleGetWordFile()}
                                  >
                                    <AddContainer>
                                      <span className="sb-title">
                                        <Translator
                                          file="module-saicaReports"
                                          name="export.word"
                                        />
                                      </span>
                                    </AddContainer>
                                  </ButtonSidebar>
                                  {/* <SidebarButton
                                    title={
                                      <AddContainer>
                                        <span>
                                          <Translator
                                            file="module-saicaReports"
                                            name="export.word"
                                          />
                                        </span>
                                      </AddContainer>
                                    }
                                    href={`${global.urlData.rootProject}saica-reports/export-word-report/${moduleBridgeHex}/${reportHex}/`}
                                    old
                                  /> */}
                                </div>
                              </OverlayTrigger>
                            </>
                          </Accordion.Body>
                        </Accordion.Item>
                      </Container>
                      <div onClick={() => setShowCompleteModal(true)}>
                        <SidebarButton
                          icon={<AppRemixIcon icon="checkbox" />}
                          title={
                            isComplete ? (
                              <Translator
                                file="module-saicaReports"
                                name="mark.as.incomplete"
                              />
                            ) : (
                              <Translator
                                file="module-saicaReports"
                                name="mark.as.complete"
                              />
                            )
                          }
                        />
                      </div>
                      <AppModal
                        show={showCompleteModal}
                        isConfirmBtn
                        isCancelBtn
                        cancelBtnText={
                          <Translator
                            file="module-saicaReports"
                            name="cancel"
                          />
                        }
                        confirmBtnText={
                          isComplete ? (
                            <Translator
                              file="module-saicaReports"
                              name="mark.as.incomplete"
                            />
                          ) : (
                            <Translator
                              file="module-saicaReports"
                              name="save.change.mark.as.complete"
                            />
                          )
                        }
                        title={
                          isComplete
                            ? ReactDOMServer.renderToString(
                                <Translator
                                  file="admin"
                                  name="mark-report-incomplete"
                                />,
                              )
                            : ReactDOMServer.renderToString(
                                <Translator
                                  file="admin"
                                  name="mark-report-complete"
                                />,
                              )
                        }
                        onClose={() => setShowCompleteModal(false)}
                        onConfirm={() => handleMarkAsComplete(values)}
                      >
                        {isComplete ? (
                          <Translator
                            file="module-saicaReports"
                            name="sure.mark.as.incomplete.report"
                          />
                        ) : (
                          <Translator
                            file="module-saicaReports"
                            name="sure.mark.as.complete.report"
                          />
                        )}
                      </AppModal>
                    </Accordion>
                  </Container>
                </Sidebar>

                <div className="row">
                  <div className="col-12">
                    <BlockTitle>
                      <Translator
                        file="module-saicaReports"
                        name="reporting.tier4.to.tier1"
                      />
                    </BlockTitle>
                    <div className="card sr-header">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-12 col-sm-6 col-md-8">
                            <span className="text-bold">
                              <Translator
                                file="module-saicaReports"
                                name="hs.framework"
                              />
                              <br />
                            </span>
                            <span className="sr-ds">
                              <Translator
                                file="module-saicaReports"
                                name="hs.ms.02.00.governance"
                              />
                            </span>
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <span className="text-bold">
                              <Translator
                                file="module-saicaReports"
                                name="requirement"
                              />
                              <br />
                            </span>
                            <span className="sr-ds">
                              <Translator
                                file="module-saicaReports"
                                name="hs.ms.02.09"
                              />
                              <br />
                            </span>
                          </div>
                        </div>
                        <br />
                        <div className="row">
                          <div className="col-12 col-sm-3 col-md-8">
                            <span className="text-bold">
                              <Translator
                                file="module-saicaReports"
                                name="tier"
                              />
                              <br />
                            </span>
                            <span className="text-wl sr-ds">
                              {report.plantGroup.masterPlant.name}
                            </span>
                          </div>
                          <div className="col-12 col-sm-3 col-md-2">
                            <span className="text-bold">
                              <Translator
                                file="module-saicaReports"
                                name="months"
                              />
                              <br />
                            </span>
                            <span className="text-wl sr-ds">
                              {report.period === 0 ? (
                                <Translator
                                  file="module-saicaReports"
                                  name="january.june"
                                />
                              ) : (
                                <Translator
                                  file="module-saicaReports"
                                  name="july.december"
                                />
                              )}
                            </span>
                          </div>
                          <div className="col-12 col-sm-3 col-md-2">
                            <span className="text-bold">
                              <Translator
                                file="module-saicaReports"
                                name="year"
                              />
                              <br />
                            </span>
                            <span className="text-wl sr-ds">{report.year}</span>
                          </div>
                        </div>
                        <br />
                        <div className="row">
                          <div className="col-12 ">
                            <div className="text-bold">
                              <Translator
                                file="module-saicaReports"
                                name="purpose"
                              />
                            </div>
                            <span className="">
                              <Translator
                                file="module-saicaReports"
                                name="to.establish.report.template.according.requirement"
                                isHtml
                              />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {(report.plantGroup.level === 4 ||
                  !_.isEqual(values.scorecard, blankScorecard)) && (
                  <>
                    <BlockTitle>
                      <Translator file="module-saicaReports" name="scorecard" />
                    </BlockTitle>
                    <BlockScorecard
                      setFieldValue={setFieldValue}
                      values={values}
                      disabled={isComplete}
                      report={report}
                      onLevelUp={(o) => handleLevelUp(o)}
                    />
                  </>
                )}

                {values.incidentsAccidents.length > 0 && (
                  <BlockTitle>
                    <Translator
                      file="module-saicaReports"
                      name="incidents.and.accidents"
                    />
                  </BlockTitle>
                )}
                <FieldArray
                  name="incidentsAccidents"
                  render={(arrayHelpers) => (
                    <div>
                      <span
                        onClick={() =>
                          arrayHelpers.push({
                            originTier: '',
                            description: '',
                            where: '',
                            ownContractor: '',
                            rootCauses: '',
                            learned: '',
                          })
                        }
                        ref={incidentsAccidentsButton}
                      />
                      {values.incidentsAccidents.map((ia, i) => (
                        <BlockIndicentsAccidents
                          index={i}
                          key={i}
                          arrayHelpers={arrayHelpers}
                          values={values}
                          disabled={isComplete}
                          report={report}
                          onLevelUp={(o) => handleLevelUp(o)}
                        />
                      ))}
                    </div>
                  )}
                />
                {values.hsMasterPlanProgress.length > 0 && (
                  <BlockTitle>
                    <Translator
                      file="module-saicaReports"
                      name="master.plan.progress"
                    />
                  </BlockTitle>
                )}
                <FieldArray
                  name="hsMasterPlanProgress"
                  render={(arrayHelpers) => (
                    <div>
                      <span
                        onClick={() =>
                          arrayHelpers.push({
                            originTier: '',
                            action: '',
                            comments: '',
                          })
                        }
                        ref={hsMasterPlanProgressButton}
                      />
                      {values.hsMasterPlanProgress.map((ia, i) => (
                        <BlockHSMasterPlanProgress
                          index={i}
                          key={i}
                          arrayHelpers={arrayHelpers}
                          values={values}
                          disabled={isComplete}
                          report={report}
                          onLevelUp={(o) => handleLevelUp(o)}
                        />
                      ))}
                    </div>
                  )}
                />
                {values.hsFrameworkProgress.length > 0 && (
                  <BlockTitle>
                    <Translator
                      file="module-saicaReports"
                      name="framework.progress"
                    />
                  </BlockTitle>
                )}
                <FieldArray
                  name="hsFrameworkProgress"
                  render={(arrayHelpers) => (
                    <div>
                      <span
                        onClick={() =>
                          arrayHelpers.push({
                            originTier: '',
                            plantName: '',
                            obligationID: '',
                            taskID: '',
                            action: '',
                            standard: '',
                            comments: '',
                          })
                        }
                        ref={hsFrameworkProgressButton}
                      />
                      {values.hsFrameworkProgress.map((ia, i) => (
                        <BlockHSFrameworkProgress
                          index={i}
                          key={i}
                          arrayHelpers={arrayHelpers}
                          values={values}
                          disabled={isComplete}
                          report={report}
                          onLevelUp={(o) => handleLevelUp(o)}
                        />
                      ))}
                    </div>
                  )}
                />
                {values.hsRiskRegister.length > 0 && (
                  <BlockTitle>
                    <Translator
                      file="module-saicaReports"
                      name="risk.register"
                    />
                  </BlockTitle>
                )}
                <FieldArray
                  name="hsRiskRegister"
                  render={(arrayHelpers) => (
                    <div>
                      <span
                        onClick={() =>
                          arrayHelpers.push({
                            originTier: '',
                            risk: '',
                            action: '',
                            rating: '',
                            comments: '',
                          })
                        }
                        ref={hsRiskRegisterButton}
                      />
                      {values.hsRiskRegister.map((ia, i) => (
                        <BlockHSRiskRegister
                          index={i}
                          key={i}
                          arrayHelpers={arrayHelpers}
                          values={values}
                          disabled={isComplete}
                          report={report}
                          onLevelUp={(o) => handleLevelUp(o)}
                        />
                      ))}
                    </div>
                  )}
                />
                {values.hsLegalRegister.length > 0 && (
                  <BlockTitle>
                    <Translator
                      file="module-saicaReports"
                      name="legal.register"
                    />
                  </BlockTitle>
                )}
                <FieldArray
                  name="hsLegalRegister"
                  render={(arrayHelpers) => (
                    <div>
                      <span
                        onClick={() =>
                          arrayHelpers.push({
                            originTier: '',
                            obligationID: '',
                            requirement: '',
                            action: '',
                            comments: '',
                          })
                        }
                        ref={hsLegalRegisterButton}
                      />
                      {values.hsLegalRegister.map((ia, i) => (
                        <BlockHSLegalRegister
                          index={i}
                          key={i}
                          arrayHelpers={arrayHelpers}
                          values={values}
                          disabled={isComplete}
                          report={report}
                          onLevelUp={(o) => handleLevelUp(o)}
                        />
                      ))}
                    </div>
                  )}
                />
                {values.otherPoints.length > 0 && (
                  <BlockTitle>
                    <Translator
                      file="module-saicaReports"
                      name="other.points"
                    />
                  </BlockTitle>
                )}
                <FieldArray
                  name="otherPoints"
                  render={(arrayHelpers) => (
                    <div>
                      <span
                        onClick={() =>
                          arrayHelpers.push({
                            originTier: '',
                            action: '',
                            comments: '',
                          })
                        }
                        ref={otherPointsButton}
                      />
                      {values.otherPoints.map((ia, i) => (
                        <BlockOtherPoints
                          index={i}
                          key={i}
                          arrayHelpers={arrayHelpers}
                          values={values}
                          disabled={isComplete}
                          report={report}
                          onLevelUp={(o) => handleLevelUp(o)}
                        />
                      ))}
                    </div>
                  )}
                />
                <Button
                  type="submit"
                  className={`btn btn-success float-right${
                    !saveLoading && !isComplete ? '' : ' disabled'
                  }`}
                  onClick={
                    !saveLoading && !isComplete
                      ? () => {
                          setFieldValue('exit', true);
                          handleSubmit();
                        }
                      : null
                  }
                >
                  <Translator file="user" name="save.and.exit" />
                </Button>
                <Button
                  type="submit"
                  className={`btn btn-primary float-right mr-2${
                    saveLoading ? ' disabled' : ''
                  }`}
                  onClick={
                    !saveLoading && !isComplete
                      ? () => {
                          setFieldValue('exit', false);
                          handleSubmit();
                        }
                      : null
                  }
                >
                  <AppRemixIcon icon="save" />
                  <Translator file="user" name="user.milestone.new" />
                </Button>
                <br />
                <br />
                <br />
              </>
            )}
          </Formik>
        )}
      </section>
      <AppModal
        show={showUpdateObligationsModal}
        isConfirmBtn={false}
        isCancelBtn
        cancelBtnText={<Translator file="module-saicaReports" name="cancel" />}
        title={
          <Translator file="module-saicaReports" name="update.legal.register" />
        }
        onClose={() => setShowUpdateObligationsModal(false)}
        onConfirm={() => handleMarkAsComplete(values)}
      >
        <p>
          <Translator
            file="module-saicaReports"
            name="update.legal.register.blocks"
            isHtml
          />
        </p>
        <p className="text-danger">
          <Translator
            file="module-saicaReports"
            name="important.delete.unsaved.modifications"
          />
        </p>
        <span
          className="btn btn-block btn-info"
          onClick={() => {
            if (!isComplete) handleUpdateReport('Obligations');
          }}
        >
          <Translator file="module-saicaReports" name="search.add.items" />
        </span>
      </AppModal>
      <AppModal
        show={showUpdateTasksModal}
        isConfirmBtn={false}
        isCancelBtn
        cancelBtnText={<Translator file="module-saicaReports" name="cancel" />}
        title={ReactDOMServer.renderToString(
          <Translator file="admin" name="update-h&s-framework-progress" />,
        )}
        onClose={() => setShowUpdateTasksModal(false)}
        onConfirm={() => handleMarkAsComplete(values)}
      >
        <p>
          <Translator
            file="module-saicaReports"
            name="update.framework.progress.blocks"
            isHtml
          />
        </p>
        <p className="text-danger">
          <Translator
            file="module-saicaReports"
            name="important.delete.unsaved.modifications"
          />
        </p>
        <span
          className="btn btn-block btn-info"
          onClick={() => {
            if (!isComplete) handleUpdateReport('Tasks');
          }}
        >
          <Translator file="module-saicaReports" name="search.add.items" />
        </span>
      </AppModal>
      <DeleteModal
        show={showRemoveModal}
        onConfirm={() => handleRemoveReport()}
        onClose={() => setShowRemoveModal(false)}
        title={<Translator file="module-saicaReports" name="confirm" />}
        text={
          <Translator file="module-saicaReports" name="confirm.delete.report" />
        }
      />
    </div>
  );
};

export default ModuleSaicaReportReportScreen;

const AddContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  span:first-child {
    display: inline-block;
    // width: 80%;
    font-size: 11px;
  }
  .badge {
    align-items: center;
    height: 14px;
  }
`;

const BlockTitle = styled.h1`
  text-align: center;
  font-size: 32px;
  margin-bottom: 15px;
`;

const Container = styled.div`
  margin-bottom: 5px;
  padding: 10px;

  .accordion-item {
    & > .panel-title > button {
      background-color: white;
      border: none;
      padding: 0;
      width: 100%;
      text-align: left;
      font-weight: bold;
      color: ${colors.primary};
    }
  }
`;

const ButtonSidebar = styled.div`
  color: #003d47;
`;


